import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useStateContext } from '../../contexts/ContextProvider';
import type { Facility } from '../../pages/settings/facilities/useFacility';

import BottomSheet from '../components/BottomSheet';
import Button from '../components/Button';
import Text from '../components/Text';
import { useFacilitesDuGroupe } from '../api/useFacilitesDuGroupe';
import { t } from '../i18n/langue';

/**
 * Bascule d'établissement — décision #25.
 *
 * **Sans proéminence.** Elle vit dans le panneau (et sur l'écran de fin de course,
 * où elle est la seule porte de sortie), jamais dans l'en-tête : elle sert
 * quelques utilisateurs multi-établissements, et lui donner du poids ferait payer
 * sa place à tout le monde.
 *
 * Elle n'existe que pour un compte rattaché à une facility **groupe** — même règle
 * que `FacilitySwitcherModal` sur le web, alimentée par le même endpoint.
 *
 * La bascule est une **ré-entrée**, pas un filtre (#25) : on repart de la racine,
 * que `ApplicationMobile` résout sur l'atterrissage du NOUVEL établissement —
 * ou sur la fin de course s'il n'y porte plus rien. Viser directement
 * `atterrissage.chemin` serait faux : à cet instant il est encore calculé sur
 * l'ancien établissement. Le mobile ne reproduit pas l'éjection vers
 * `/settings/facilities` du web : les réglages ne sont pas portés, ce serait une
 * impasse (#17 §4).
 *
 * Une feuille montante, et rien d'autre : sur 390 px une boîte centrée n'est
 * qu'une feuille avec des marges perdues, et elle met ses boutons au milieu de
 * l'écran, là où le pouce n'est pas (#34).
 */
interface SelecteurEtablissementProps {
    /** Referme la surface qui porte le sélecteur — le panneau, quand il y en a un. */
    onBascule?: () => void;
}

export default function SelecteurEtablissement({ onBascule }: SelecteurEtablissementProps) {
    const { currentFacility, setCurrentFacility, user } = useStateContext();
    const navigate = useNavigate();
    const [ouvert, setOuvert] = useState(false);

    const idGroupe = user?.facility?.is_group ? user.facility.id : null;
    const { data: groupe, isLoading } = useFacilitesDuGroupe(ouvert ? idGroupe : null);

    // Pas de groupe : un seul établissement possible, donc pas de bascule à offrir.
    if (!idGroupe) return null;

    const choisir = (facility: Facility) => {
        const changement = facility.id !== currentFacility?.id;
        // Le endpoint `with-children` nomme la liste `child_facilities` ; le reste
        // de l'app lit `facilities` sur `currentFacility`. Même remappage que le web.
        const enfants = (facility as Facility & { child_facilities?: Facility[] })
            .child_facilities;
        setCurrentFacility(enfants && !facility.facilities ? { ...facility, facilities: enfants } : facility);
        setOuvert(false);

        if (changement) {
            onBascule?.();
            navigate('/', { replace: true });
        }
    };

    const etablissements: Facility[] = groupe
        ? [groupe, ...(groupe.child_facilities ?? [])]
        : [];

    return (
        <>
            <Button
                libelle={t('coque.changerEtablissement')}
                variante="discret"
                onPress={() => setOuvert(true)}
                alignement="debut"
                espacement="aucun"
                className="underline"
            />

            <BottomSheet
                ouverte={ouvert}
                onClose={() => setOuvert(false)}
                libelle={t('coque.choisirEtablissement')}
                libelleFermeture={t('coque.fermerChoixEtablissement')}
            >
                <Text variante="sousTitre" as="h2" className="mb-3">
                    {t('coque.etablissement')}
                </Text>

                {isLoading && (
                    <Text variante="petit" ton="attenue">
                        {t('commun.chargement')}
                    </Text>
                )}

                <ul className="flex flex-col">
                    {etablissements.map((facility) => (
                        <li key={facility.id}>
                            <Button
                                libelle={
                                    facility.id === currentFacility?.id
                                        ? t('coque.etablissementSelectionne', { nom: facility.name ?? '' })
                                        : (facility.name ?? t('coque.sansNom'))
                                }
                                variante={facility.id === currentFacility?.id ? 'secondaire' : 'discret'}
                                onPress={() => choisir(facility)}
                                pleineLargeur
                                alignement="debut"
                            />
                        </li>
                    ))}
                </ul>
            </BottomSheet>
        </>
    );
}
